/**
 * ReservationPopover - Sprint 27 + Sprint 29 + Sprint 32
 * Clic a la card del calendari → Popover amb info extra de la reserva.
 * Sprint 29: badge de pagament + botons Efectiu/Targeta/Transferència per reserves PENDING directes.
 * Sprint 32: mobile full-screen overlay (en lloc de Popover que queda tallat).
 *
 * Runbook 7, F2: el comportament es tria pel `pointerType` de l'últim gest, no per l'amplada.
 *   - Ratolí: passar-hi per sobre ~300 ms obre un `HoverCard` amb el cos; un clic obre la fitxa.
 *   - Tàctil / llapis: com abans. Tap → cos a pantalla completa (< 768 px) o en popover.
 *   L'amplada (`useIsMobile`) només decideix si el cos es pinta a pantalla completa.
 *
 * Tanda «dia a dia» (§3): fila Welcome · Codi · Trucar (`QuickWhatsAppActions`) on hi havia el
 * telèfon en brut, «via …» a la línia del canal, «Pagat:» en una fila i menys separadors.
 *
 * Repàs visual §8 (P14, P15, P25): etiquetes `CALENDAR_PALETTE.popoverLabel` a 11 px, la franja
 * de pagament diu «Pagament: pendent» i el `referer` `setBooking JSON` es pinta «via PMS vell».
 *
 * Senyals de la card (`plans/calendar-card-signals.md` §6 F2): fila «Operativa» (entrada, sortida,
 * no-show i neteja), link o transferència demanats, «Paga en arribar», «Qui paga» i «WhatsApp: té /
 * no té / no se sap». Tot surt de `data.signals`; sense `signals` (calendari històric), res de nou.
 *
 * Bloquejar a l'habitació (`plans/reservation-move-lock.md` §4 F.1): fila sota «Operativa» per
 * bloquejar (motiu obligatori) o desbloquejar (alerta amb el motiu), i franja vermella a dalt si
 * Beds24 l'ha moguda estant bloquejada. Surt de `signals.moveLock`.
 *
 * Enllaç de pagament (`plans/stripe-payment-links.md` F5): «Crear enllaç de pagament» a petició
 * (només ADMIN) o l'estat de l'enllaç obert, sota els botons de «Pagat:». Viu a `components/payments/`.
 *
 * Hora de sortida pactada (portes v1, sprint 06 §4): «surt a les 14:00» sota el check-out, si n'hi ha.
 * Surt de `GET /reservations/:id` (`agreedCheckoutTime`), la consulta que el cos ja fa.
 *
 * Disseny: mobile-first, Shadcn Popover/HoverCard + Tailwind
 */

import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import * as HoverCardPrimitive from '@radix-ui/react-hover-card';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { Popover, PopoverContent } from '@/components/ui/popover';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { format, isValid } from 'date-fns';
import { cn } from '@/lib/utils';
import { langToLocale } from '@/lib/dates';
import { pickGuestFormUrl } from '@/lib/messaging';
import { agreedCheckoutLabel } from '@/lib/agreed-checkout';
import { QuickWhatsAppActions } from '@/components/messaging/QuickWhatsAppActions';
import { ReservationPaymentOrder } from '@/components/payments/ReservationPaymentOrder';
import { useAuth } from '@/contexts/AuthContext';
import { usePaymentOrders, useReservationOutstanding } from '@/hooks/usePaymentOrders';
import {
  needsBillingRefresh,
  reservationBillingBadge,
  useRefreshBillingOncePerOpen,
  useRefreshReservationBilling,
  useReservationBillingStatus,
} from '@/lib/billing';
import {
  OUTSIDE_STRIPE_WARNING,
  PMS_LEDGER_PENDING_TITLE,
  formatCents,
  pickOpenOrder,
  popoverPaymentAmounts,
} from '@/lib/payment-orders';
import { ReservationCell, type ReservationCellData } from './ReservationCell';
import {
  usePaymentMutation,
  type PaymentMethod,
  type PaymentStatus,
} from '@/hooks/usePaymentMutation';
import { api } from '@/services/api';
import { Copy, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import type { BookingSignals, PaymentRequestedVia } from '@guilleumes/types';
import { BedConfigEditor } from './BedConfigEditor';
import { CALENDAR_PALETTE, normalizeChannelKey } from './calendar-theme';
import { MOVE_LOCK_ALERT_MARK, MOVE_LOCK_MARK } from './signal-styles';
import { useMoveLockMutation } from '@/hooks/useMoveLockMutation';
import { useIsMobile } from '@/hooks/useIsMobile';
import {
  useReservationStayMutation,
  type ReservationStayVariables,
} from '@/hooks/useReservationStayMutation';
import { useRoomCleanMutation, type RoomCleanVariables } from '@/hooks/useRoomCleanMutation';
import {
  useReservationMetadataMutation,
  type ReservationMetadataPatch,
} from '@/hooks/useReservationMetadataMutation';
import { useClients } from '@/hooks/useClients';
import { normalizePointerType, type GridPointerType, type LastGesture } from './grid/useGridPointer';

// ============================================
// TYPES
// ============================================

interface ReservationPopoverProps {
  data: ReservationCellData;
  /** Callback quan l'usuari vol obrir el Sheet de detalls complets */
  onOpenSheet?: (id: string) => void;
  className?: string;
  /** Per al marc de fora (l'anell de selecció): el radi ha de casar amb el de la card. */
  style?: CSSProperties;
  /**
   * L'últim gest de la graella (`useGridPointer`). Decideix què fa el clic: ratolí → fitxa,
   * tàctil/llapis → cos d'informació; si no era un tap (s'arrossegava), res. Sense aquesta
   * prop, el component mira el `pointerType` del seu propi `pointerdown`.
   */
  getLastGesture?: () => LastGesture;
  /** `true` mentre la graella s'arrossega: el HoverCard no s'obre (i es tanca si ho era). */
  hoverDisabled?: boolean;
}

// ============================================
// HELPERS
// ============================================

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('ca-ES', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}

/** Euros → «230,00 €», amb el mateix format que la resta d'imports de pagament (`formatCents`). */
function formatPopoverEuros(euros: number): string {
  return formatCents(Math.round(euros * 100));
}

function getChannelEmoji(channel: string): string {
  const map: Record<string, string> = {
    'Booking.com': '🔵',
    Expedia: '🟡',
    Airbnb: '🔴',
    Direct: '🟢',
    Directe: '🟢',
    Agoda: '🟠',
    'Hotels.com': '🔴',
  };
  return map[channel] || '⚪';
}

/**
 * El nom del canal que es pinta. `setBooking JSON` és l'`apiSource` de les reserves que entren
 * des del PMS vell (JOAN): es pinta «PMS vell».
 */
export function channelDisplayLabel(apiSource: string | null | undefined, channel: string): string {
  const raw = apiSource || channel;
  return raw === 'setBooking JSON' ? 'PMS vell' : raw;
}

/**
 * El «via …» de la línia del canal (repàs visual §8, P25). El `referer` `setBooking JSON` és el
 * del PMS vell: «via PMS vell». Sense `referer`, o si diu el mateix que el canal, no n'hi ha.
 */
export function refererDisplayLabel(
  referer: string | null | undefined,
  channel: string,
): string | null {
  if (!referer || normalizeChannelKey(referer) === normalizeChannelKey(channel)) return null;
  return referer === 'setBooking JSON' ? 'PMS vell' : referer;
}

/** La línia sencera del canal: «Direct · via PMS vell», o només el canal si el «via» no afegeix res. */
export function channelLineLabel(
  apiSource: string | null | undefined,
  channel: string,
  referer: string | null | undefined,
): string {
  const label = channelDisplayLabel(apiSource, channel);
  const via = refererDisplayLabel(referer, channel);
  return via && via !== label ? `${label} · via ${via}` : label;
}

function getStatusLabel(status?: string): { label: string; color: string } {
  const map: Record<string, { label: string; color: string }> = {
    confirmed: { label: 'Confirmada', color: 'text-green-700 bg-green-50' },
    pending: { label: 'Pendent', color: 'text-yellow-700 bg-yellow-50' },
    cancelled: { label: 'Cancel·lada', color: 'text-red-700 bg-red-50' },
    checked_in: { label: 'Check-in fet', color: 'text-blue-700 bg-blue-50' },
    checked_out: { label: 'Check-out fet', color: 'text-gray-700 bg-gray-100' },
    no_show: { label: "No s'ha presentat", color: 'text-red-700 bg-red-50' },
  };
  return map[status || 'confirmed'] || { label: status || '?', color: 'text-gray-600 bg-gray-50' };
}

// ============================================
// PAYMENT BADGE (with undo for PAID)
// ============================================

interface PaymentBadgeProps {
  paymentStatusRaw?: ReservationCellData['paymentStatusRaw'];
  paymentMethod?: ReservationCellData['paymentMethod'];
  /** El pendent a ensenyar, en euros (`popoverPaymentAmounts`: el llibre del PMS o Beds24). */
  balance?: number | null;
  /** El títol del «Pendent» (d'on surt), si no és el de Beds24. */
  pendingTitle?: string;
  paidAt?: string | null;
  /** Needed for undo — only required when paymentStatusRaw === 'PAID' */
  reservationId?: string;
  onUndoSuccess?: () => void;
}

function PaymentBadge({
  paymentStatusRaw,
  paymentMethod,
  balance,
  pendingTitle,
  paidAt,
  reservationId,
  onUndoSuccess,
}: PaymentBadgeProps) {
  const [confirmUndo, setConfirmUndo] = useState(false);
  const { mutate, isPending } = usePaymentMutation();

  if (!paymentStatusRaw) return null;

  const config: Record<
    NonNullable<ReservationCellData['paymentStatusRaw']>,
    { label: string; color: string; icon: string }
  > = {
    // Repàs visual §8 (P15): en minúscula darrere de «Pagament:», perquè no es confongui amb
    // l'estat de la reserva del capçal («Pendent» a tots dos llocs).
    PAID: { label: 'pagat', color: 'text-green-700 bg-green-50 border-green-200', icon: '✅' },
    PENDING: {
      label: 'pendent',
      color: 'text-orange-700 bg-orange-50 border-orange-200',
      icon: '🟠',
    },
    PARTIAL: {
      label: 'parcial',
      color: 'text-yellow-700 bg-yellow-50 border-yellow-200',
      icon: '🟡',
    },
    REFUNDED: {
      label: 'retornat',
      color: 'text-gray-600 bg-gray-50 border-gray-200',
      icon: '↩️',
    },
  };

  const methodLabels: Partial<Record<NonNullable<ReservationCellData['paymentMethod']>, string>> = {
    CASH: 'Efectiu',
    CARD: 'Targeta',
    TRANSFER: 'Transferència',
    STRIPE: 'Stripe',
    BOOKING_OTA: 'Booking.com',
    EXPEDIA_OTA: 'Expedia',
  };

  const cfg = config[paymentStatusRaw];
  const methodLabel = paymentMethod ? methodLabels[paymentMethod] : null;

  const handleUndo = () => {
    if (!reservationId) return;
    mutate(
      { reservationId, payload: { status: 'PENDING' } },
      {
        onSuccess: () => {
          setConfirmUndo(false);
          onUndoSuccess?.();
        },
        onError: () => {
          setConfirmUndo(false);
        },
      },
    );
  };

  // Confirmation state: "Segur? Sí / No"
  if (confirmUndo) {
    return (
      <div className="flex items-center gap-1.5 px-2 py-1 rounded border text-xs text-orange-700 bg-orange-50 border-orange-200">
        <span className="text-[10px] font-medium">Desfer pagament?</span>
        <button
          className="ml-auto text-[10px] font-semibold text-red-600 hover:text-red-800 disabled:opacity-50 px-1"
          disabled={isPending}
          onClick={handleUndo}
        >
          {isPending ? '...' : '✅ Sí'}
        </button>
        <button
          className="text-[10px] font-semibold text-gray-500 hover:text-gray-700 px-1"
          disabled={isPending}
          onClick={() => setConfirmUndo(false)}
        >
          ❌ No
        </button>
      </div>
    );
  }

  return (
    <div className={cn('flex items-center gap-1.5 px-2 py-1 rounded border text-xs', cfg.color)}>
      <span>{cfg.icon}</span>
      <span>
        Pagament: <span className="font-semibold">{cfg.label}</span>
      </span>
      {methodLabel && <span className="text-[10px] opacity-75">· {methodLabel}</span>}
      {balance != null && balance > 0 && paymentStatusRaw !== 'PAID' && (
        <span className="text-[10px] opacity-75 ml-auto" title={pendingTitle}>
          Pendent: {formatPopoverEuros(balance)}
        </span>
      )}
      {paidAt && paymentStatusRaw === 'PAID' && (
        <span className="text-[10px] opacity-75 ml-auto">
          {new Date(paidAt).toLocaleDateString('ca-ES', { day: 'numeric', month: 'short' })}
        </span>
      )}
      {/* Undo button — only for PAID reservations */}
      {paymentStatusRaw === 'PAID' && reservationId && (
        <button
          className="ml-auto text-[10px] text-gray-400 hover:text-orange-600 transition-colors px-0.5"
          title="Desfer pagament"
          onClick={() => setConfirmUndo(true)}
        >
          ↩️
        </button>
      )}
    </div>
  );
}

// ============================================
// PAYMENT ACTION BUTTONS
// ============================================

/**
 * Mostra botons de pagament per a TOTES les reserves PENDING/PARTIAL.
 * Inclou botons Efectiu, Targeta, Transferència, Stripe i un botó OTA dinàmic per canal.
 * Hostel World: usa el botó Targeta (cobren via número de tarja al TPV).
 */
interface PaymentActionsProps {
  reservationId: string;
  paymentStatusRaw?: ReservationCellData['paymentStatusRaw'];
  channel: string;
  onSuccess?: () => void;
}

// Map channel name → OTA payment method + label (`short`: el text del botó, que ha de cabre a la fila)
const CHANNEL_OTA_MAP: Record<
  string,
  { method: PaymentMethod; label: string; short: string; emoji: string }
> = {
  'Booking.com': { method: 'BOOKING_OTA', label: 'Booking.com', short: 'Booking', emoji: '🔵' },
  Expedia: { method: 'EXPEDIA_OTA', label: 'Expedia', short: 'Expedia', emoji: '🟡' },
  // Hostel World: NOT here — they send a credit card number, must be charged via TPV (use CARD button)
  // Agoda / Hotels.com: use BOOKING_OTA as generic
  Agoda: { method: 'BOOKING_OTA', label: 'Agoda', short: 'Agoda', emoji: '🟠' },
  'Hotels.com': { method: 'BOOKING_OTA', label: 'Hotels.com', short: 'Hotels', emoji: '🔴' },
};

/** Els mètodes de sempre, en l'ordre de sempre. `label` és el nom sencer (títol i lector de pantalla). */
const PAYMENT_BUTTONS: {
  method: PaymentMethod;
  emoji: string;
  short: string;
  label: string;
  className: string;
}[] = [
  { method: 'CASH', emoji: '💵', short: 'Efectiu', label: 'Efectiu', className: 'border-green-300 text-green-700 hover:bg-green-50' },
  { method: 'CARD', emoji: '💳', short: 'Targeta', label: 'Targeta', className: 'border-blue-300 text-blue-700 hover:bg-blue-50' },
  { method: 'TRANSFER', emoji: '🏦', short: 'Transf.', label: 'Transferència', className: 'border-purple-300 text-purple-700 hover:bg-purple-50' },
  { method: 'STRIPE', emoji: '⚡', short: 'Stripe', label: 'Stripe', className: 'border-indigo-300 text-indigo-700 hover:bg-indigo-50' },
];

function PaymentActions({
  reservationId,
  paymentStatusRaw,
  channel,
  onSuccess,
}: PaymentActionsProps) {
  const { mutate, isPending } = usePaymentMutation();
  const [localError, setLocalError] = useState<string | null>(null);

  // Mostrar botons per a TOTES les reserves PENDING/PARTIAL (incloses OTA)
  const needsPayment = paymentStatusRaw === 'PENDING' || paymentStatusRaw === 'PARTIAL';
  if (!needsPayment) return null;

  const handleMark = (method: PaymentMethod, status: PaymentStatus = 'PAID') => {
    setLocalError(null);
    mutate(
      { reservationId, payload: { status, method } },
      {
        onSuccess: () => {
          onSuccess?.();
        },
        onError: (err) => {
          setLocalError(err.message || 'Error en marcar el pagament');
        },
      },
    );
  };

  // Botó OTA dinàmic per canal (si el canal és una OTA coneguda)
  const otaInfo = CHANNEL_OTA_MAP[channel];

  // Tanda «dia a dia» §3.2: tot en una fila. Els botons s'encongeixen i, si no hi caben, el text
  // es talla; el nom sencer és al títol i a l'`aria-label`.
  const buttons = [
    ...PAYMENT_BUTTONS,
    ...(otaInfo
      ? [
          {
            method: otaInfo.method,
            emoji: otaInfo.emoji,
            short: otaInfo.short,
            label: `Cobrat per ${otaInfo.label}`,
            className: 'border-gray-300 text-gray-700 hover:bg-gray-50',
          },
        ]
      : []),
  ];

  return (
    <div className="flex flex-col gap-1">
      <div
        className="flex items-center gap-1 min-w-0"
        role="group"
        aria-label="Marcar com a pagat"
      >
        <span
          className="text-[10px] font-medium shrink-0"
          style={{ color: CALENDAR_PALETTE.popoverLabel }}
        >
          Pagat:
        </span>
        {buttons.map((b) => (
          <Button
            key={b.method + b.short}
            size="sm"
            variant="outline"
            className={cn('text-[10px] h-6 px-1 gap-0.5 flex-1 min-w-0', b.className)}
            disabled={isPending}
            onClick={() => handleMark(b.method)}
            title={b.label}
            aria-label={b.label}
          >
            <span aria-hidden="true">{b.emoji}</span>
            <span className="truncate min-w-0">{b.short}</span>
          </Button>
        ))}
      </div>
      {isPending && <p className="text-[10px] text-blue-500 text-center">Guardant...</p>}
      {localError && <p className="text-[10px] text-red-500 text-center">{localError}</p>}
    </div>
  );
}

// ============================================
// OPERATIVA (senyals de la card, §6 F2.1)
// ============================================

/** Text de la confirmació del «No s'ha presentat» (§6 F2.1). */
export const NO_SHOW_CONFIRM_TEXT =
  "La porta deixarà d'acceptar el seu codi. Si és d'una OTA, marca-ho també a l'extranet perquè pagui la penalització.";

export type OperationAction =
  | 'check-in'
  | 'undo-check-in'
  | 'check-out'
  | 'undo-check-out'
  | 'no-show'
  | 'undo-no-show'
  | 'clean'
  | 'undo-clean';

export interface OperationButton {
  action: OperationAction;
  /** Text visible del botó. */
  label: string;
  /** Nom sencer (títol i `aria-label`). */
  title: string;
}

export interface OperationsState {
  /** Frase d'estat abans dels botons («Ha marxat», «No s'ha presentat»), si n'hi ha. */
  stayNote: string | null;
  /** «Habitació 101 neta» quan ja està marcada avui. */
  cleanNote: string | null;
  buttons: OperationButton[];
}

function isSameLocalDay(iso: string, now: Date): boolean {
  const d = new Date(iso);
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
}

/**
 * Quins botons surten a la fila «Operativa». Pur: l'estat surt de `signals.raw` (i de l'estat de la
 * reserva per als casos antics sense hora, com `CHECKED_OUT` escrit per Miranda).
 */
export function computeOperations(
  signals: BookingSignals,
  status: ReservationCellData['status'] | undefined,
  now: Date = new Date(),
): OperationsState {
  const { raw, stay } = signals;
  const st = status as string | undefined;
  const noShow = stay.light === 'NO_SHOW' || st === 'no_show';
  const left = !noShow && (!!raw.checkedOutAt || stay.light === 'LEFT' || st === 'checked_out');
  const entered = stay.light === 'IN' || !!raw.checkedInAt || st === 'checked_in';

  const buttons: OperationButton[] = [];
  let stayNote: string | null = null;

  if (noShow) {
    stayNote = "No s'ha presentat";
    buttons.push({ action: 'undo-no-show', label: 'Desfer', title: "Desfer «No s'ha presentat»" });
  } else if (left) {
    stayNote = 'Ha marxat';
    buttons.push({ action: 'undo-check-out', label: 'Desfer sortida', title: 'Desfer sortida' });
  } else {
    if (raw.checkedInAt) {
      buttons.push({ action: 'undo-check-in', label: 'Desfer entrada', title: 'Desfer entrada' });
    } else if (stay.light !== 'IN') {
      buttons.push({ action: 'check-in', label: 'Ha entrat', title: 'Ha entrat' });
    }
    buttons.push({ action: 'check-out', label: 'Ha marxat', title: 'Ha marxat' });
    if (!entered) {
      buttons.push({ action: 'no-show', label: "No s'ha presentat", title: "No s'ha presentat" });
    }
  }

  let cleanNote: string | null = null;
  if (raw.physicalRoomId) {
    const roomLabel = `Habitació ${raw.physicalRoomName ?? ''}`.trim();
    const markedClean =
      !!raw.lastCleanedAt && isSameLocalDay(raw.lastCleanedAt, now) && stay.light !== 'ARRIVING_DIRTY';
    if (markedClean) {
      cleanNote = `${roomLabel} neta`;
      buttons.push({ action: 'undo-clean', label: 'Desfer', title: `Desfer «${roomLabel} neta»` });
    } else {
      buttons.push({ action: 'clean', label: `${roomLabel} neta`, title: `${roomLabel} neta` });
    }
  }

  return { stayNote, cleanNote, buttons };
}

/** Cada acció → la crida que toca. `confirm-no-show` = abans cal la confirmació. */
export type OperationRequest =
  | { kind: 'stay'; vars: ReservationStayVariables }
  | { kind: 'clean'; vars: RoomCleanVariables }
  | { kind: 'confirm-no-show' };

export function operationRequest(
  action: OperationAction,
  reservationId: string,
  signals: BookingSignals,
): OperationRequest {
  const physicalRoomId = signals.raw.physicalRoomId ?? '';
  switch (action) {
    case 'check-in':
      return { kind: 'stay', vars: { reservationId, state: 'CHECKED_IN' } };
    case 'check-out':
      return { kind: 'stay', vars: { reservationId, state: 'CHECKED_OUT' } };
    case 'no-show':
      return { kind: 'confirm-no-show' };
    case 'undo-check-in':
    case 'undo-check-out':
    case 'undo-no-show':
      return { kind: 'stay', vars: { reservationId, state: 'UNDO' } };
    case 'clean':
      return { kind: 'clean', vars: { physicalRoomId, clean: true, reservationId } };
    case 'undo-clean':
      return { kind: 'clean', vars: { physicalRoomId, clean: false, reservationId } };
  }
}

/** Executa una acció de la fila amb els `mutate` dels hooks. */
export function dispatchOperation(
  request: OperationRequest,
  handlers: {
    stay: (vars: ReservationStayVariables) => void;
    clean: (vars: RoomCleanVariables) => void;
    askNoShow: () => void;
  },
): void {
  if (request.kind === 'stay') handlers.stay(request.vars);
  else if (request.kind === 'clean') handlers.clean(request.vars);
  else handlers.askNoShow();
}

export interface OperationsRowProps {
  state: OperationsState;
  confirmingNoShow: boolean;
  pending: boolean;
  onAction: (action: OperationAction) => void;
  onConfirmNoShow: () => void;
  onCancelNoShow: () => void;
}

/** La fila «Operativa», sense hooks (els tests la poden cridar com una funció). */
export function OperationsRow({
  state,
  confirmingNoShow,
  pending,
  onAction,
  onConfirmNoShow,
  onCancelNoShow,
}: OperationsRowProps) {
  return (
    <div className="flex flex-col gap-1" data-testid="reservation-operations">
      <p
        className="text-[11px] font-medium uppercase tracking-wide"
        style={{ color: CALENDAR_PALETTE.popoverLabel }}
      >
        Operativa
      </p>
      {(state.stayNote || state.cleanNote) && (
        <p className="text-[10px] text-gray-700">
          {[state.stayNote, state.cleanNote].filter(Boolean).join(' · ')}
        </p>
      )}
      {confirmingNoShow ? (
        <div
          className="flex flex-col gap-1 px-2 py-1.5 rounded border text-[10px] text-red-800 bg-red-50 border-red-200"
          role="alertdialog"
          aria-label="Confirmar «No s'ha presentat»"
        >
          <span>{NO_SHOW_CONFIRM_TEXT}</span>
          <div className="flex gap-1 justify-end">
            <Button
              size="sm"
              variant="outline"
              className="h-6 px-2 text-[10px] border-red-300 text-red-700 hover:bg-red-100"
              disabled={pending}
              onClick={onConfirmNoShow}
              aria-label="Sí, no s'ha presentat"
            >
              Sí, no s'ha presentat
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-6 px-2 text-[10px]"
              disabled={pending}
              onClick={onCancelNoShow}
              aria-label="Cancel·lar"
            >
              Cancel·lar
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-1" role="group" aria-label="Operativa">
          {state.buttons.map((b) => (
            <Button
              key={b.action}
              size="sm"
              variant="outline"
              className="h-6 px-1.5 text-[10px]"
              disabled={pending}
              onClick={() => onAction(b.action)}
              title={b.title}
              aria-label={b.title}
            >
              {b.label}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}

function ReservationOperations({
  reservationId,
  signals,
  status,
}: {
  reservationId: string;
  signals: BookingSignals;
  status: ReservationCellData['status'] | undefined;
}) {
  const [confirmingNoShow, setConfirmingNoShow] = useState(false);
  const stayMutation = useReservationStayMutation();
  const cleanMutation = useRoomCleanMutation();
  const state = computeOperations(signals, status);

  const handleAction = (action: OperationAction) => {
    dispatchOperation(operationRequest(action, reservationId, signals), {
      stay: (vars) => stayMutation.mutate(vars),
      clean: (vars) => cleanMutation.mutate(vars),
      askNoShow: () => setConfirmingNoShow(true),
    });
  };

  return (
    <OperationsRow
      state={state}
      confirmingNoShow={confirmingNoShow}
      pending={stayMutation.isPending || cleanMutation.isPending}
      onAction={handleAction}
      onConfirmNoShow={() =>
        stayMutation.mutate(
          { reservationId, state: 'NO_SHOW' },
          { onSettled: () => setConfirmingNoShow(false) },
        )
      }
      onCancelNoShow={() => setConfirmingNoShow(false)}
    />
  );
}

// ============================================
// BLOQUEIG A L'HABITACIÓ (`plans/reservation-move-lock.md` §4 F.1)
// ============================================

export type MoveLock = BookingSignals['moveLock'];

/** Dreceres del diàleg de bloquejar (L1): omplen el text del motiu. */
export const MOVE_LOCK_SHORTCUTS = [
  'Vistes',
  'Llits separats',
  'Llit gran',
  'La mateixa habitació de sempre',
] as const;

export const MOVE_LOCK_REASON_MAX = 200;

/** El motiu: obligatori, sense espais als extrems, 1-200 caràcters (el mateix que valida el backend). */
export const moveLockSchema = z.object({
  reason: z
    .string()
    .trim()
    .min(1, 'Cal un motiu')
    .max(MOVE_LOCK_REASON_MAX, `Màxim ${MOVE_LOCK_REASON_MAX} caràcters`),
});

export type MoveLockFormValues = z.infer<typeof moveLockSchema>;

/** «per Pere, 30 set. a les 21:58», amb `date-fns` i el locale català. Sense nom ni data, `null`. */
export function moveLockByline(lock: Pick<MoveLock, 'lockedAt' | 'lockedByName'>): string | null {
  const date = lock.lockedAt ? new Date(lock.lockedAt) : null;
  const when =
    date && isValid(date) ? format(date, "d MMM 'a les' HH:mm", { locale: langToLocale('ca') }) : null;
  const parts = [lock.lockedByName, when].filter(Boolean);
  return parts.length > 0 ? `per ${parts.join(', ')}` : null;
}

export interface MoveLockRowProps {
  lock: MoveLock;
  pending: boolean;
  onLockClick: () => void;
  onUnlockClick: () => void;
}

/** La fila del bloqueig, sota «Operativa». Sense hooks (els tests la criden directament). */
export function MoveLockRow({ lock, pending, onLockClick, onUnlockClick }: MoveLockRowProps) {
  if (!lock.reason) {
    return (
      <div className="flex items-center gap-1 min-w-0" data-testid="reservation-move-lock">
        <Button
          size="sm"
          variant="outline"
          className="h-6 px-1.5 text-[10px]"
          disabled={pending}
          onClick={onLockClick}
          title="Bloquejar la reserva a aquesta habitació"
          aria-label="Bloquejar l'habitació"
        >
          <span aria-hidden="true">{MOVE_LOCK_MARK.glyph}</span>
          Bloquejar l'habitació
        </Button>
      </div>
    );
  }

  const byline = moveLockByline(lock);
  return (
    <div className="flex items-start gap-1 min-w-0" data-testid="reservation-move-lock">
      <div className="flex-1 min-w-0">
        <p className="text-[11px] font-medium break-words" style={{ color: MOVE_LOCK_MARK.color }}>
          {MOVE_LOCK_MARK.glyph} Bloquejada: {lock.reason}
        </p>
        {byline && <p className="text-[10px] text-gray-500">{byline}</p>}
      </div>
      <Button
        size="sm"
        variant="outline"
        className="h-6 px-1.5 text-[10px] shrink-0"
        disabled={pending}
        onClick={onUnlockClick}
        aria-label="Desbloquejar"
      >
        Desbloquejar
      </Button>
    </div>
  );
}

export interface MoveLockAlertStripProps {
  lock: MoveLock;
  pending: boolean;
  onDismiss: () => void;
}

/** Franja vermella de dalt: Beds24 l'ha moguda estant bloquejada. Sense avís, res. */
export function MoveLockAlertStrip({ lock, pending, onDismiss }: MoveLockAlertStripProps) {
  if (!lock.alert) return null;
  return (
    <div
      className="flex flex-col gap-1 px-2 py-1.5 rounded border text-[11px] text-red-800 bg-red-50 border-red-200"
      role="alert"
      data-testid="reservation-move-lock-alert"
    >
      <span
        className="self-start rounded px-1 text-[10px] font-semibold leading-4"
        style={{ background: MOVE_LOCK_ALERT_MARK.background, color: MOVE_LOCK_ALERT_MARK.color }}
      >
        {MOVE_LOCK_ALERT_MARK.glyph} {MOVE_LOCK_ALERT_MARK.text}
      </span>
      <span className="break-words">{lock.alert}</span>
      {lock.reason && <span className="break-words font-medium">Motiu: {lock.reason}</span>}
      <Button
        size="sm"
        variant="outline"
        className="h-6 px-2 text-[10px] self-end border-red-300 text-red-700 hover:bg-red-100"
        disabled={pending}
        onClick={onDismiss}
        aria-label="Tancar l'avís"
      >
        Tancar l'avís
      </Button>
    </div>
  );
}

/**
 * Els diàlegs van per sobre de la pantalla completa del tàctil (`z-[200]`). Són fills (a l'arbre de
 * React) del HoverCard o del popover: els seus esdeveniments no surten del cos d'informació.
 */
const MOVE_LOCK_DIALOG_Z = 'z-[210]';

export interface LockMoveDialogProps {
  open: boolean;
  pending: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (reason: string) => void;
}

/** «Bloquejar l'habitació»: dreceres de L1 + motiu obligatori (React Hook Form + Zod). */
export function LockMoveDialog({ open, pending, onOpenChange, onSubmit }: LockMoveDialogProps) {
  const form = useForm<MoveLockFormValues>({
    resolver: zodResolver(moveLockSchema),
    defaultValues: { reason: '' },
  });
  const reason = useWatch({ control: form.control, name: 'reason' }) ?? '';
  const { errors } = form.formState;
  const submit = form.handleSubmit((values) => onSubmit(values.reason));

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) form.reset({ reason: '' });
        onOpenChange(next);
      }}
    >
      <DialogContent className={cn('sm:max-w-md', MOVE_LOCK_DIALOG_Z)}>
        <DialogHeader>
          <DialogTitle>Bloquejar la reserva a aquesta habitació</DialogTitle>
          <DialogDescription>
            Des del calendari ja no es podrà arrossegar. Per moure-la caldrà llegir el motiu.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="flex flex-col gap-3">
          <div className="flex flex-wrap gap-1" role="group" aria-label="Dreceres del motiu">
            {MOVE_LOCK_SHORTCUTS.map((text) => (
              <Button
                key={text}
                type="button"
                size="sm"
                variant={reason.trim() === text ? 'default' : 'outline'}
                className="h-7 px-2 text-xs"
                onClick={() => form.setValue('reason', text, { shouldValidate: true })}
                aria-label={text}
              >
                {text}
              </Button>
            ))}
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="move-lock-reason">Motiu</Label>
            <Textarea
              id="move-lock-reason"
              rows={3}
              maxLength={MOVE_LOCK_REASON_MAX}
              placeholder="Per què no s'ha de moure?"
              {...form.register('reason')}
            />
            {errors.reason && <p className="text-xs text-destructive">{errors.reason.message}</p>}
          </div>
          <DialogFooter>
            <Button type="submit" disabled={pending || reason.trim() === ''}>
              Bloquejar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export interface UnlockMoveDialogProps {
  open: boolean;
  reason: string;
  pending: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}

/** «Desbloquejar aquesta reserva?» amb el motiu en gran (L4). */
export function UnlockMoveDialog({ open, reason, pending, onOpenChange, onConfirm }: UnlockMoveDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className={cn('w-full max-w-[calc(100%-2rem)] rounded-lg sm:max-w-md', MOVE_LOCK_DIALOG_Z)}>
        <AlertDialogHeader>
          <AlertDialogTitle>Desbloquejar aquesta reserva?</AlertDialogTitle>
          <AlertDialogDescription className="text-lg font-bold text-gray-900 break-words">
            Es va bloquejar per: {reason}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}>Cancel·lar</AlertDialogCancel>
          <AlertDialogAction disabled={pending} onClick={onConfirm}>
            Desbloquejar
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** La fila del bloqueig amb els seus diàlegs i la crida. */
function ReservationMoveLock({
  reservationId,
  lock,
  onDone,
}: {
  reservationId: string;
  lock: MoveLock;
  /** Després de bloquejar o desbloquejar: es tanca el cos (el HoverCard no ha de quedar penjat). */
  onDone: () => void;
}) {
  const [dialog, setDialog] = useState<'lock' | 'unlock' | null>(null);
  const { mutate, isPending } = useMoveLockMutation({
    onSuccess: () => {
      setDialog(null);
      onDone();
    },
  });

  return (
    <>
      <MoveLockRow
        lock={lock}
        pending={isPending}
        onLockClick={() => setDialog('lock')}
        onUnlockClick={() => setDialog('unlock')}
      />
      <LockMoveDialog
        open={dialog === 'lock'}
        pending={isPending}
        onOpenChange={(open) => setDialog(open ? 'lock' : null)}
        onSubmit={(reason) => mutate({ reservationId, action: { kind: 'lock', reason } })}
      />
      <UnlockMoveDialog
        open={dialog === 'unlock'}
        reason={lock.reason ?? ''}
        pending={isPending}
        onOpenChange={(open) => setDialog(open ? 'unlock' : null)}
        onConfirm={() => mutate({ reservationId, action: { kind: 'unlock' } })}
      />
    </>
  );
}

function ReservationMoveLockAlert({ reservationId, lock }: { reservationId: string; lock: MoveLock }) {
  const { mutate, isPending } = useMoveLockMutation();
  return (
    <MoveLockAlertStrip
      lock={lock}
      pending={isPending}
      onDismiss={() => mutate({ reservationId, action: { kind: 'dismiss-alert' } })}
    />
  );
}

// ============================================
// PAGAMENT: LINK / TRANSFERÈNCIA, PAGA EN ARRIBAR I QUI PAGA (§6 F2.2)
// ============================================

const PAYMENT_REQUESTED_LABELS: Record<PaymentRequestedVia, { button: string; note: string }> = {
  STRIPE_LINK: { button: 'Link enviat', note: 'Link enviat el' },
  TRANSFER: { button: 'Transferència demanada', note: 'Transferència demanada el' },
};

/** Dia d'un instant (`paymentRequestedAt`), amb `date-fns` i el locale català («28 set.»). */
export function formatRequestedDate(iso: string): string {
  const date = new Date(iso);
  return isValid(date) ? format(date, 'd MMM', { locale: langToLocale('ca') }) : '';
}

/** El `PATCH` de «Link enviat» / «Transferència demanada» (ara) i el de «Treure». */
export function paymentRequestPatch(
  via: PaymentRequestedVia | null,
  now: Date = new Date(),
): ReservationMetadataPatch {
  if (via === null) return { paymentRequestedAt: null };
  return { paymentRequestedAt: now.toISOString(), paymentRequestedVia: via };
}

export type PayOnArrivalChoice = 'yes' | 'no' | 'inherit';

export function payOnArrivalChoice(override: boolean | null): PayOnArrivalChoice {
  return override === true ? 'yes' : override === false ? 'no' : 'inherit';
}

export function payOnArrivalPatch(choice: PayOnArrivalChoice): ReservationMetadataPatch {
  return { payOnArrival: choice === 'yes' ? true : choice === 'no' ? false : null };
}

export interface PaymentSignalsRowProps {
  signals: BookingSignals;
  /** Hi ha pagament pendent (PENDING o PARTIAL): surten els botons de link i transferència. */
  needsPayment: boolean;
  /**
   * La reserva té una sol·licitud de pagament OBERTA (enllaç del PMS). Decisió del 14-OCT-2026: un
   * sol botó d'«enviat». Llavors l'antic «Link enviat» no surt: el seu «Marca com enviat» apunta la
   * sol·licitud i també aquest senyal (`STRIPE_LINK`), al backend i en la mateixa operació. Sense
   * sol·licitud oberta, «Link enviat» segueix com sempre (enllaços fets a mà). «Transferència
   * demanada» i «Treure» no canvien; «Treure» no desmarca la sol·licitud.
   */
  hasOpenPaymentOrder?: boolean;
  pending: boolean;
  onPatch: (patch: ReservationMetadataPatch) => void;
}

/** Les vies de «pagament demanat» que el popover ofereix a mà. */
export function manualRequestVias(hasOpenPaymentOrder: boolean): readonly PaymentRequestedVia[] {
  return hasOpenPaymentOrder ? ['TRANSFER'] : ['STRIPE_LINK', 'TRANSFER'];
}

/** Link o transferència demanats i «Paga en arribar», sense hooks (els tests la criden directament). */
export function PaymentSignalsRow({
  signals,
  needsPayment,
  hasOpenPaymentOrder = false,
  pending,
  onPatch,
}: PaymentSignalsRowProps) {
  const { raw } = signals;
  const choice = payOnArrivalChoice(raw.payOnArrivalOverride);
  const requestedVia = raw.paymentRequestedVia ?? 'STRIPE_LINK';
  const choices: { value: PayOnArrivalChoice; label: string; title: string }[] = [
    { value: 'yes', label: 'Sí', title: 'Paga en arribar: sí' },
    { value: 'no', label: 'No', title: 'Paga en arribar: no' },
    {
      value: 'inherit',
      label: 'Com la fitxa',
      title:
        choice === 'inherit'
          ? `Paga en arribar: com la fitxa del client (ara, ${signals.payOnArrival ? 'sí' : 'no'})`
          : 'Paga en arribar: com la fitxa del client',
    },
  ];

  return (
    <div className="flex flex-col gap-1" data-testid="reservation-payment-signals">
      {raw.paymentRequestedAt ? (
        <div className="flex items-center gap-1 min-w-0 text-[10px] text-gray-700">
          <span className="truncate">
            {PAYMENT_REQUESTED_LABELS[requestedVia].note} {formatRequestedDate(raw.paymentRequestedAt)}
          </span>
          <Button
            size="sm"
            variant="ghost"
            className="h-5 px-1 text-[10px] ml-auto"
            disabled={pending}
            onClick={() => onPatch(paymentRequestPatch(null))}
            title="Treure la petició de pagament"
            aria-label="Treure"
          >
            Treure
          </Button>
        </div>
      ) : (
        needsPayment && (
          <div className="flex items-center gap-1 min-w-0" role="group" aria-label="Pagament demanat">
            {manualRequestVias(hasOpenPaymentOrder).map((via) => (
              <Button
                key={via}
                size="sm"
                variant="outline"
                className="h-6 px-1.5 text-[10px] flex-1 min-w-0"
                disabled={pending}
                onClick={() => onPatch(paymentRequestPatch(via))}
                title={PAYMENT_REQUESTED_LABELS[via].button}
                aria-label={PAYMENT_REQUESTED_LABELS[via].button}
              >
                <span className="truncate">{PAYMENT_REQUESTED_LABELS[via].button}</span>
              </Button>
            ))}
          </div>
        )
      )}

      <div className="flex items-center gap-1 min-w-0" role="group" aria-label="Paga en arribar (aquesta reserva)">
        <span
          className="text-[10px] font-medium shrink-0"
          style={{ color: CALENDAR_PALETTE.popoverLabel }}
        >
          Paga en arribar (aquesta reserva):
        </span>
        {choices.map((c) => (
          <Button
            key={c.value}
            size="sm"
            variant={choice === c.value ? 'default' : 'outline'}
            className="h-5 px-1.5 text-[10px]"
            disabled={pending}
            aria-pressed={choice === c.value}
            onClick={() => {
              if (choice !== c.value) onPatch(payOnArrivalPatch(c.value));
            }}
            title={c.title}
            aria-label={c.title}
          >
            {c.label}
          </Button>
        ))}
      </div>
    </div>
  );
}

/** Buscador d'empreses en línia (sense un segon popover: el HoverCard es tancaria en sortir-ne). */
function CompanyPicker({
  onPick,
  onCancel,
  disabled,
}: {
  onPick: (clientId: string) => void;
  onCancel: () => void;
  disabled: boolean;
}) {
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(term.trim()), 300);
    return () => clearTimeout(timer);
  }, [term]);

  const { data, isLoading } = useClients({
    page: 1,
    searchTerm: debounced,
    actiuFilter: 'all',
    perPage: 10,
  });
  const companies = debounced.length >= 2
    ? (data?.data ?? []).filter((c) => c.tipus_client === 'empresa')
    : [];

  return (
    <div className="flex flex-col gap-1 rounded border border-gray-200 p-1.5">
      <div className="flex items-center gap-1">
        <Input
          className="h-6 flex-1 min-w-0 px-1.5 py-0 text-[11px] md:text-[11px]"
          placeholder="Busca l'empresa (nom o NIF)"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          aria-label="Busca l'empresa"
          autoFocus
        />
        <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[10px]" onClick={onCancel}>
          Cancel·lar
        </Button>
      </div>
      {debounced.length < 2 ? (
        <p className="text-[10px] text-gray-500">Escriu almenys 2 lletres</p>
      ) : isLoading ? (
        <p className="text-[10px] text-gray-500">Buscant…</p>
      ) : companies.length === 0 ? (
        <p className="text-[10px] text-gray-500">Cap empresa trobada</p>
      ) : (
        <ul className="flex flex-col max-h-32 overflow-y-auto">
          {companies.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                className="w-full text-left text-[11px] px-1 py-0.5 rounded hover:bg-gray-100 truncate disabled:opacity-50"
                disabled={disabled}
                onClick={() => onPick(c.id)}
              >
                🏢 {c.empresa || c.nom_complet}
                {c.nif_empresa ? <span className="text-gray-500"> · {c.nif_empresa}</span> : null}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ReservationPaymentSignals({
  reservationId,
  signals,
  needsPayment,
  clientId,
  payerName,
}: {
  reservationId: string;
  signals: BookingSignals;
  needsPayment: boolean;
  /** `metadata.clientId` de la reserva (de `GET /reservations/:id`), si ja s'ha carregat. */
  clientId: string | null | undefined;
  payerName: string | undefined;
}) {
  const [picking, setPicking] = useState(false);
  const { mutate, isPending } = useReservationMetadataMutation();
  // La mateixa consulta (i clau) que el bloc de l'enllaç de pagament: no en fa cap de més. Només
  // ADMIN veu les sol·licituds; per a la resta, «Link enviat» com sempre.
  const { user } = useAuth();
  const { data: orders } = usePaymentOrders(reservationId, user?.role === 'ADMIN');
  const hasOpenPaymentOrder = pickOpenOrder(orders) !== null;
  const patch = (p: ReservationMetadataPatch, onDone?: () => void) =>
    mutate({ reservationId, patch: p }, { onSuccess: () => onDone?.() });
  const hasPayer = !!clientId;

  return (
    <div className="flex flex-col gap-1">
      <PaymentSignalsRow
        signals={signals}
        needsPayment={needsPayment}
        hasOpenPaymentOrder={hasOpenPaymentOrder}
        pending={isPending}
        onPatch={(p) => patch(p)}
      />
      <div className="flex items-center gap-1 min-w-0" role="group" aria-label="Qui paga">
        <span
          className="text-[10px] font-medium shrink-0"
          style={{ color: CALENDAR_PALETTE.popoverLabel }}
        >
          Qui paga:
        </span>
        <Button
          size="sm"
          variant={!hasPayer ? 'default' : 'outline'}
          className="h-5 px-1.5 text-[10px]"
          disabled={isPending || clientId === undefined}
          aria-pressed={!hasPayer}
          onClick={() => {
            setPicking(false);
            if (hasPayer) patch({ clientId: null });
          }}
        >
          L'hoste
        </Button>
        <Button
          size="sm"
          variant={hasPayer ? 'default' : 'outline'}
          className="h-5 px-1.5 text-[10px] min-w-0"
          disabled={isPending || clientId === undefined}
          aria-pressed={hasPayer}
          onClick={() => setPicking(true)}
          title={hasPayer && payerName ? `Una empresa: ${payerName} (canviar)` : 'Una empresa'}
        >
          <span className="truncate">{hasPayer && payerName ? `🏢 ${payerName}` : 'Una empresa'}</span>
        </Button>
      </div>
      {picking && (
        <CompanyPicker
          disabled={isPending}
          onCancel={() => setPicking(false)}
          onPick={(id) => patch({ clientId: id }, () => setPicking(false))}
        />
      )}
    </div>
  );
}

/** «WhatsApp: té / no té / no se sap», a partir de `hasWhatsapp` (a la cel·la, `whatsappSent`). */
export function whatsappStatusLabel(hasWhatsapp: boolean | null | undefined): string {
  return hasWhatsapp === true ? 'té' : hasWhatsapp === false ? 'no té' : 'no se sap';
}

// ============================================
// POPOVER CONTENT
// ============================================

/**
 * El cos d'informació d'una reserva (abans `PopoverBody`). El mateix al HoverCard (ratolí), al
 * popover i a la pantalla completa (tàctil); els botons de dins funcionen a tots tres.
 */
export function ReservationInfoBody({
  data,
  onOpenSheet,
  onClose,
  fullScreen = false,
}: {
  data: ReservationCellData;
  onOpenSheet?: (id: string) => void;
  onClose: () => void;
  fullScreen?: boolean;
}) {
  const {
    id,
    beds24Id,
    guestName,
    channel,
    checkIn,
    checkOut,
    numNights,
    numAdults,
    numChildren,
    arrivalTime,
    totalPrice,
    lang,
    status,
    guestPhone,
    guestCountry,
    guestCountry2,
    whatsappSent,
    isPaid,
    paymentStatusRaw,
    paymentMethod,
    beds,
    bedConfigRaw,
    bedConfigSource,
    bedWarning,
    balance,
    paidAt,
    primaryGuestName,
    companyName,
    isVip,
    isGroup,
    hasPet,
    referer,
    apiSource,
    guestPhoneE164,
    beds24PropertyId,
    signals,
  } = data;

  const [copiedFormUrl, setCopiedFormUrl] = useState(false);

  // Obtenir el link del formulari de registre
  const { data: reservationFull } = useQuery({
    queryKey: ['reservation-full', id],
    queryFn: async () => {
      const res = await api.get(`/reservations/${id}`);
      return res.data;
    },
    staleTime: 60_000,
  });

  // El formulari que encara obre (ni caducat ni revocat): el Registre en pot haver creat un de nou.
  const formUrl = pickGuestFormUrl(reservationFull?.accessTokens);
  // Hora de sortida pactada (portes v1): una cancel·lada ja no surt.
  const agreedCheckout = status !== 'cancelled' ? agreedCheckoutLabel(reservationFull?.agreedCheckoutTime) : null;

  // «Pendent» i «Import (pendent/pagat)» (decisió del 14-OCT-2026): amb cobraments per enllaç, el
  // llibre del PMS, que és l'únic que els veu (mai s'apunten a Beds24); sense, el `balance` de
  // Beds24 de sempre. Només ADMIN pot llegir el llibre, i no cal per a una reserva ja pagada.
  const { user } = useAuth();
  const { data: pmsOutstanding } = useReservationOutstanding(
    id,
    user?.role === 'ADMIN' && !!signals && !!paymentStatusRaw && paymentStatusRaw !== 'PAID',
  );
  const amounts = popoverPaymentAmounts({
    balance,
    isPaid,
    paymentStatusRaw,
    outstanding: pmsOutstanding,
  });
  // Facturació (plans/factura-emesa-reserva-facturada.md, decisions 3 i 7): l'estat calculat dels
  // documents fiscals, a tots els canals. Només ADMIN i només amb el cos muntat (popover obert, reserva
  // del PMS): si no, cap petició.
  const { data: billingStatus } = useReservationBillingStatus(id, user?.role === 'ADMIN' && !!signals);
  const billingBadge = reservationBillingBadge(billingStatus);
  // Un esborrany (o un estat que no se sap) es posa al dia amb Cuentica una sola vegada per obertura: la
  // Carme el pot haver emès a la web. Un error no toca el popover (el hook no fa cap toast).
  const { mutate: refreshBilling } = useRefreshReservationBilling();
  useRefreshBillingOncePerOpen(id, needsBillingRefresh(billingStatus), refreshBilling);

  const handleCopyFormUrl = async () => {
    if (!formUrl) return;
    try {
      await navigator.clipboard.writeText(formUrl);
      setCopiedFormUrl(true);
      toast.success('Enllaç copiat');
      setTimeout(() => setCopiedFormUrl(false), 2000);
    } catch {
      toast.error("No s'ha pogut copiar l'enllaç");
    }
  };

  const statusInfo = getStatusLabel(status);
  // Bloqueig (§4 F.1): només a les vives i amb un backend que ja l'envia.
  const liveSignals = status !== 'cancelled' && signals?.moveLock ? signals : undefined;
  const channelLine = channelLineLabel(apiSource, channel, referer);

  const handleOpenSheet = () => {
    onClose();
    onOpenSheet?.(id);
  };

  return (
    <div
      className={cn(
        // El cos fa scroll i el peu de botons queda sempre a la vista (G4): el contenidor que
        // limita l'alçada (HoverCard, popover o pantalla completa) ha de ser `flex flex-col`.
        'flex flex-col min-h-0 flex-1',
        fullScreen ? 'w-full' : 'min-w-[260px] max-w-[320px]',
      )}
    >
      <div
        // §3.2: `gap-2` i menys separadors, perquè una reserva normal hi càpiga sense scroll.
        className="flex flex-col gap-2 min-h-0 flex-1 overflow-y-auto"
        data-testid="reservation-info-scroll"
      >
        {/* Avís de Beds24 (bloqueig §4 F.1): a dalt de tot, fins que algú el tanqui. */}
        {liveSignals?.moveLock.alert && (
          <ReservationMoveLockAlert reservationId={id} lock={liveSignals.moveLock} />
        )}

        {/* Header */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <p
              className={cn(
                'font-bold text-sm text-gray-900 truncate',
                status === 'cancelled' && 'line-through opacity-60',
              )}
            >
              {guestName}
            </p>
            {primaryGuestName && primaryGuestName !== guestName && (
              <p className="text-xs text-blue-600 truncate">👤 {primaryGuestName}</p>
            )}
            {companyName && <p className="text-xs text-gray-500 truncate">🏢 {companyName}</p>}
          </div>
          <span
            className={cn(
              'text-[10px] font-semibold px-1.5 py-0.5 rounded-full flex-shrink-0',
              statusInfo.color,
            )}
          >
            {statusInfo.label}
          </span>
        </div>

        {/* Badges */}
        {(isVip || isGroup || hasPet) && (
          <div className="flex gap-1 flex-wrap">
            {isVip && (
              <span className="text-[10px] bg-yellow-100 text-yellow-800 px-1.5 py-0.5 rounded font-medium">
                ⭐ VIP
              </span>
            )}
            {isGroup && (
              <span className="text-[10px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded font-medium">
                👥 GRUP
              </span>
            )}
            {hasPet && (
              <span className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded font-medium">
                🐕 Mascota
              </span>
            )}
          </div>
        )}

        {/* Divider */}
        <div className="border-t border-gray-100" />

        {/* Dates & Stay */}
        <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
          <InfoRow icon="📅" label="Check-in" value={formatDate(checkIn)} />
          <InfoRow icon="📅" label="Check-out" value={formatDate(checkOut)} />
          {agreedCheckout && <InfoRow icon="🕑" label="Hora pactada" value={agreedCheckout} />}
          {numNights != null && (
            <InfoRow icon="🌙" label="Nits" value={`${numNights} nit${numNights !== 1 ? 's' : ''}`} />
          )}
          {arrivalTime && <InfoRow icon="🕐" label="Arribada" value={arrivalTime} />}
          <InfoRow
            icon="👥"
            label="Hostes"
            value={`${numAdults} adult${numAdults !== 1 ? 's' : ''}${numChildren > 0 ? ` + ${numChildren} nen${numChildren !== 1 ? 's' : ''}` : ''}`}
          />
          {lang && <InfoRow icon="🌐" label="Idioma" value={lang.toUpperCase()} />}
          {guestCountry && (
            <InfoRow
              icon={guestCountry2 ? `🏳️` : '🌍'}
              label="País"
              value={guestCountry2 ? `${guestCountry2} ${guestCountry}` : guestCountry}
            />
          )}
        </div>

        {/* Senyals de la card (§6 F2.1): entrada, sortida, no-show i neteja. Sense `signals`, res. */}
        {signals && <ReservationOperations reservationId={id} signals={signals} status={status} />}

        {/* Bloqueig a l'habitació (§4 F.1). Les cancel·lades no se'n bloquegen. */}
        {liveSignals && (
          <ReservationMoveLock reservationId={id} lock={liveSignals.moveLock} onDone={onClose} />
        )}

        {/* 🛏️ Sprint 31 — Llits & Mascota (editor inline). Sense separador: ja porta el rètol. */}
        <div className="flex flex-col gap-1">
          <p
            className="text-[11px] font-medium uppercase tracking-wide"
            style={{ color: CALENDAR_PALETTE.popoverLabel }}
          >
            Llits
          </p>
          <BedConfigEditor
            reservationId={id}
            currentBeds={beds ?? []}
            hasOverride={bedConfigRaw != null}
            hasPet={hasPet ?? false}
            bedConfigSource={bedConfigSource ?? null}
            bedWarning={bedWarning ?? null}
            onSaved={onClose}
          />
        </div>

        {/* Divider */}
        <div className="border-t border-gray-100" />

        {/* Canal & Pagament. «via {referer}» va a la línia del canal (§3.2). */}
        <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
          <InfoRow
            icon={getChannelEmoji(channel)}
            label="Canal"
            value={channelLine}
            className={channelLine !== channelDisplayLabel(apiSource, channel) ? 'col-span-2' : undefined}
          />
          {beds24Id && <InfoRow icon="🆔" label="Beds24" value={beds24Id} />}
          {totalPrice != null && (
            <InfoRow
              icon={amounts.isPaid ? '✅' : '🟠'}
              label="Import"
              value={`${formatPopoverEuros(totalPrice)}${amounts.isPaid === true ? ' (pagat)' : amounts.isPaid === false ? ' (pendent)' : ''}`}
            />
          )}
          {billingBadge && (
            <InfoRow icon="🧾" label="Factura" value={billingBadge.text} className="col-span-2" />
          )}
        </div>

        {/* 🆕 Sprint 29 — Pagament: estat (amb desfer si és PAID) i, si cal, «Pagat:» en una fila */}
        {paymentStatusRaw && (
          <div className="flex flex-col gap-1">
            <PaymentBadge
              paymentStatusRaw={paymentStatusRaw}
              paymentMethod={paymentMethod}
              balance={amounts.pendingEuros}
              pendingTitle={amounts.source === 'PMS_LEDGER' ? PMS_LEDGER_PENDING_TITLE : undefined}
              paidAt={paidAt}
              reservationId={id}
              onUndoSuccess={onClose}
            />
            {amounts.outsideStripeWarning && (
              <p
                className="text-[10px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-0.5"
                role="note"
                data-testid="payment-outside-stripe-warning"
              >
                {OUTSIDE_STRIPE_WARNING}
              </p>
            )}
            {(paymentStatusRaw === 'PENDING' || paymentStatusRaw === 'PARTIAL') && (
              <PaymentActions
                reservationId={id}
                paymentStatusRaw={paymentStatusRaw}
                channel={channel}
                onSuccess={onClose}
              />
            )}
          </div>
        )}

        {/* Enllaç de pagament (Stripe, F5): a petició, només ADMIN. Amb un enllaç obert, el seu estat.
            Sense `signals` (calendari històric), res: allò no són reserves del PMS. */}
        {signals && (
          <ReservationPaymentOrder
            reservationId={id}
            canCreate={
              status !== 'cancelled' &&
              (paymentStatusRaw === 'PENDING' || paymentStatusRaw === 'PARTIAL') &&
              signals.payment.via !== 'OTA_BOOKING' &&
              signals.payment.via !== 'OTA_EXPEDIA'
            }
            phone={guestPhone}
            phoneE164={guestPhoneE164}
          />
        )}

        {/* Senyals de la card (§6 F2.2): link o transferència, paga en arribar i qui paga. */}
        {signals && (
          <ReservationPaymentSignals
            reservationId={id}
            signals={signals}
            // La cobra Booking o Expedia: no cal ni link ni transferència.
            needsPayment={
              (paymentStatusRaw === 'PENDING' || paymentStatusRaw === 'PARTIAL') &&
              signals.payment.via !== 'OTA_BOOKING' &&
              signals.payment.via !== 'OTA_EXPEDIA'
            }
            clientId={
              reservationFull === undefined ? undefined : (reservationFull?.metadata?.clientId ?? null)
            }
            payerName={companyName}
          />
        )}

        {/* Contacte: Welcome · Codi · Trucar + telèfon, i l'estat de WhatsApp (tanda «dia a dia» §3.1) */}
        <div className="border-t border-gray-100" />
        <div className="flex flex-col gap-1">
          <QuickWhatsAppActions
            reservationId={id}
            phone={guestPhone}
            phoneE164={guestPhoneE164}
            beds24PropertyId={beds24PropertyId}
          />
          {/* WhatsApp (senyals de la card §6 F2.3): `whatsappSent` és el `hasWhatsapp` de la
              reserva. Només diu si en té; el botó que canviava `contactat` ja no hi és. */}
          <div
            className="flex items-center gap-1 min-w-0 text-[10px] text-gray-500"
            data-testid="reservation-whatsapp-status"
          >
            <span className="flex-shrink-0">
              {whatsappSent === true ? '🟢' : whatsappSent === false ? '🔴' : '⚪'}
            </span>
            <span>WhatsApp:</span>
            <span className="text-gray-700 font-medium">{whatsappStatusLabel(whatsappSent)}</span>
          </div>
        </div>
      </div>

      {/* Actions: fora de l'àrea amb scroll, sempre a la vista */}
      <div
        className="border-t border-gray-100 pt-2 mt-2 flex gap-2 shrink-0"
        data-testid="reservation-info-footer"
      >
        <Button
          size="sm"
          variant="default"
          className="flex-1 text-xs h-7"
          onClick={handleOpenSheet}
        >
          Veure detalls complets →
        </Button>
        {/* Copiar link formulari de registre */}
        <Button
          size="sm"
          variant="outline"
          className="text-xs h-7 px-2"
          onClick={handleCopyFormUrl}
          disabled={!formUrl}
          title={formUrl ? 'Copiar link formulari de registre' : 'No hi ha formulari generat'}
        >
          {copiedFormUrl ? (
            <Check className="h-3.5 w-3.5 text-green-600" />
          ) : (
            <Copy className="h-3.5 w-3.5" />
          )}
        </Button>
        {/* A pantalla completa ja hi ha la creu de la barra de dalt: només una. */}
        {!fullScreen && (
          <Button
            size="sm"
            variant="ghost"
            className="text-xs h-7 px-2"
            onClick={onClose}
            aria-label="Tancar"
          >
            ✕
          </Button>
        )}
      </div>
    </div>
  );
}

// ============================================
// INFO ROW HELPER
// ============================================

function InfoRow({
  icon,
  label,
  value,
  className,
}: {
  icon: string;
  label: string;
  value: string;
  className?: string;
}) {
  return (
    <div className={cn('flex items-start gap-1 min-w-0', className)}>
      <span className="text-xs flex-shrink-0">{icon}</span>
      <div className="min-w-0">
        <span
          className="text-[11px] block leading-none"
          style={{ color: CALENDAR_PALETTE.popoverLabel }}
        >
          {label}
        </span>
        <span className="text-xs text-gray-800 font-medium truncate block" title={value}>
          {value}
        </span>
      </div>
    </div>
  );
}

// ============================================
// MAIN COMPONENT
// ============================================

/**
 * Hi ha cap diàleg de Radix obert (la fitxa, el formulari de nova reserva, una confirmació)?
 * Llavors el HoverCard no s'obre: taparia el diàleg i no hi ha res a mirar a sota.
 */
export function isAnyDialogOpen(
  root: Pick<Document, 'querySelector'> | undefined = typeof document !== 'undefined'
    ? document
    : undefined,
): boolean {
  return !!root?.querySelector(
    '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]',
  );
}

interface BodyHostProps {
  data: ReservationCellData;
  onOpenSheet?: (id: string) => void;
  onClose: () => void;
}

/** Els clics i `pointerdown` de dins no han d'arribar a la graella (el React propaga pels portals). */
const stopGridEvents = {
  onClick: (e: React.MouseEvent) => e.stopPropagation(),
  onPointerDown: (e: React.PointerEvent) => e.stopPropagation(),
  onMouseDown: (e: React.MouseEvent) => e.stopPropagation(),
};

/** Ratolí: el contingut del HoverCard. Ha de viure dins d'un `HoverCard` (obert). */
export function ReservationHoverContent({ data, onOpenSheet, onClose }: BodyHostProps) {
  return (
    <HoverCardContent
      className="w-auto p-3 max-h-[var(--radix-hover-card-content-available-height)] flex flex-col overflow-hidden"
      side="bottom"
      align="start"
      sideOffset={4}
      collisionPadding={8}
      data-testid="reservation-hover-card"
      {...stopGridEvents}
    >
      <ReservationInfoBody data={data} onOpenSheet={onOpenSheet} onClose={onClose} />
    </HoverCardContent>
  );
}

/**
 * Tàctil a pantalla estreta: el cos a pantalla completa (Sprint 32).
 * Runbook 7, R3: es munta en un portal a `document.body`. Dins de la graella, un avantpassat amb
 * `transform`/`backdrop-filter` fa de bloc contenidor del `fixed` i la fitxa començava a y = 120.
 * Els esdeveniments sintètics de React pugen igualment per l'arbre de React fins a la graella:
 * per això es mantenen els `stopGridEvents`. Sense `document` (render de servidor, tests), inline.
 */
export function ReservationFullScreen({ data, onOpenSheet, onClose }: BodyHostProps) {
  const screen = (
    <div
      className="fixed inset-0 z-[200] flex flex-col bg-white"
      data-testid="reservation-fullscreen"
      {...stopGridEvents}
    >
      {/* Top bar amb botó close */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white sticky top-0 z-10">
        <span className="text-sm font-semibold text-gray-700 truncate pr-2">{data.guestName}</span>
        <button
          className="flex-shrink-0 p-1.5 rounded-full hover:bg-gray-100 transition-colors"
          onClick={onClose}
          aria-label="Tancar"
        >
          <X className="h-5 w-5 text-gray-600" />
        </button>
      </div>

      {/* Contingut scrollable */}
      <div className="flex-1 min-h-0 flex flex-col px-4 py-3">
        <ReservationInfoBody data={data} onOpenSheet={onOpenSheet} onClose={onClose} fullScreen />
      </div>
    </div>
  );
  if (typeof document === 'undefined') return screen;
  return createPortal(screen, document.body);
}

/**
 * Què fa un clic a la reserva, segons l'últim gest. Pur, per als tests.
 * `ignore` = no era un tap (s'arrossegava la graella).
 */
export function resolveReservationClick(
  last: LastGesture | undefined,
  ownPointerType: GridPointerType | null,
): 'open-sheet' | 'show-info' | 'ignore' {
  if (last && !last.wasTap) return 'ignore';
  const pointerType = last?.pointerType ?? ownPointerType ?? 'mouse';
  return pointerType === 'mouse' ? 'open-sheet' : 'show-info';
}

/**
 * G4: després de deixar anar (un arrossegament, el clic que tanca una confirmació…) el HoverCard
 * no s'obre fins que el ratolí es torna a moure. En deixar anar, el navegador envia un
 * `pointerenter` a la card que queda sota el punter sense que l'usuari hagi fet res.
 * Un sol joc de listeners al `document` per a totes les cards (en fase de captura, abans que
 * els handlers de React), instal·lat un sol cop per pàgina.
 * R7: tancar una confirmació amb Esc no fa `pointerup`: `keydown` d'`Escape` també desarma.
 */
let hoverArmed = true;
let hoverArmListenersInstalled = false;

/** Per als tests: el HoverCard es pot obrir ara mateix? */
export function isHoverArmed(): boolean {
  return hoverArmed;
}

export function installHoverArmListeners() {
  if (hoverArmListenersInstalled || typeof document === 'undefined') return;
  hoverArmListenersInstalled = true;
  document.addEventListener(
    'pointerup',
    () => {
      hoverArmed = false;
    },
    true,
  );
  document.addEventListener(
    'keydown',
    (e) => {
      if (e.key === 'Escape') hoverArmed = false;
    },
    true,
  );
  document.addEventListener(
    'pointermove',
    (e) => {
      // Chrome envia `pointermove` sense moviment després de canvis del DOM: no compten.
      if (e.pointerType === 'mouse' && (e.movementX !== 0 || e.movementY !== 0)) hoverArmed = true;
    },
    true,
  );
}

/** Retard d'obertura del HoverCard (el mateix que `openDelay`). */
const HOVER_OPEN_DELAY_MS = 300;

export function ReservationPopover({
  data,
  onOpenSheet,
  className,
  style,
  getLastGesture,
  hoverDisabled = false,
}: ReservationPopoverProps) {
  const [hoverOpen, setHoverOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const isMobile = useIsMobile();
  const triggerRef = useRef<HTMLDivElement>(null);
  const ownPointerTypeRef = useRef<GridPointerType | null>(null);
  /** El Radix ha volgut obrir el HoverCard sense moviment: s'obrirà quan el ratolí es mogui. */
  const pendingHoverRef = useRef(false);
  const pendingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    installHoverArmListeners();
    return () => {
      if (pendingTimerRef.current) clearTimeout(pendingTimerRef.current);
    };
  }, []);

  // Si la graella comença a arrossegar-se amb el HoverCard obert, es tanca (i no torna sol).
  if (hoverDisabled && hoverOpen) setHoverOpen(false);

  const clearPendingHover = () => {
    pendingHoverRef.current = false;
    if (pendingTimerRef.current) {
      clearTimeout(pendingTimerRef.current);
      pendingTimerRef.current = null;
    }
  };

  const canOpenHover = () => !hoverDisabled && !infoOpen && !isAnyDialogOpen();

  const handleHoverChange = (next: boolean) => {
    if (!next) {
      clearPendingHover();
      setHoverOpen(false);
      return;
    }
    if (!canOpenHover()) return;
    if (!hoverArmed) {
      pendingHoverRef.current = true;
      return;
    }
    setHoverOpen(true);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (e.pointerType !== 'mouse' || !pendingHoverRef.current || !hoverArmed) return;
    pendingHoverRef.current = false;
    if (pendingTimerRef.current) clearTimeout(pendingTimerRef.current);
    pendingTimerRef.current = setTimeout(() => {
      pendingTimerRef.current = null;
      if (canOpenHover()) setHoverOpen(true);
    }, HOVER_OPEN_DELAY_MS);
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const action = resolveReservationClick(getLastGesture?.(), ownPointerTypeRef.current);
    if (action === 'ignore') return;
    setHoverOpen(false);
    if (action === 'open-sheet') {
      onOpenSheet?.(data.id);
      return;
    }
    setInfoOpen((open) => !open);
  };

  const closeInfo = () => setInfoOpen(false);
  const closeHover = () => setHoverOpen(false);

  const cell: ReactNode = (
    <div
      ref={triggerRef}
      className={cn('w-full h-full', className)}
      style={style}
      onClick={handleClick}
      onPointerDown={(e) => {
        ownPointerTypeRef.current = normalizePointerType(e.pointerType);
        clearPendingHover();
      }}
      onPointerMove={handlePointerMove}
      onPointerLeave={clearPendingHover}
    >
      <ReservationCell data={data} className="h-full" />
    </div>
  );

  return (
    <>
      <HoverCard
        open={hoverOpen && !hoverDisabled && !infoOpen}
        onOpenChange={handleHoverChange}
        openDelay={300}
        closeDelay={150}
      >
        {/* Tàctil/llapis a pantalla ampla: el popover de sempre, ancorat a la card. */}
        <Popover open={infoOpen && !isMobile} onOpenChange={setInfoOpen}>
          <PopoverPrimitive.Anchor asChild>
            <HoverCardTrigger asChild>{cell}</HoverCardTrigger>
          </PopoverPrimitive.Anchor>
          <PopoverContent
            className="p-3 z-50 max-h-[var(--radix-popover-content-available-height)] flex flex-col overflow-hidden"
            side="bottom"
            align="start"
            sideOffset={4}
            collisionPadding={8}
            // Un tap a la mateixa card ja la tanca (`handleClick`): que no la tanqui també el «fora».
            onInteractOutside={(e) => {
              if (e.target instanceof Node && triggerRef.current?.contains(e.target)) {
                e.preventDefault();
              }
            }}
            {...stopGridEvents}
          >
            <ReservationInfoBody data={data} onOpenSheet={onOpenSheet} onClose={closeInfo} />
          </PopoverContent>
        </Popover>

        {/* Ratolí: HoverCard en un portal, perquè la graella té overflow i transforms. */}
        <HoverCardPrimitive.Portal>
          <ReservationHoverContent data={data} onOpenSheet={onOpenSheet} onClose={closeHover} />
        </HoverCardPrimitive.Portal>
      </HoverCard>

      {/* Tàctil/llapis a pantalla estreta: pantalla completa. */}
      {infoOpen && isMobile && (
        <ReservationFullScreen data={data} onOpenSheet={onOpenSheet} onClose={closeInfo} />
      )}
    </>
  );
}

export default ReservationPopover;
